import { FaCheck } from "react-icons/fa";
import { useEmpresa, useGrupoPermissao, useTipoSistema } from "../hooks/DropdownHooks";
import { useState, useEffect } from "react";
import { MdLockOutline, MdOutlineAccountCircle } from "react-icons/md";
import { TipoValidacao, ValidacaoUtils } from "../utils/ValidacaoUtils";
import { useNavigate, useParams } from "react-router-dom";
import { APP_ROUTES } from "../services/app_routes";
import DropdownPadrao from "../components/DropdownPadrao/DropdownPadrao";
import FormularioPadrao from "../components/FormularioPadrao/FormularioPadrao";
import FormularioPadraoExpansivo from "../components/FormularioPadraoExpansivo/FormularioPadraoExpansivo";
import CheckboxPadrao from "../components/CheckboxPadrao/CheckboxPadrao";
import InputPadrao from "../components/InputPadrao/InputPadrao";
import TogglePadrao from "../components/TogglePadrao/TogglePadrao";
import Mensagens from "../utils/MensagemUtils";
import Carregamento from "../components/Carregamento/Carregamento";
import './Cadastros.css';
import { TipoFormatacao } from "../utils/FormatacaoUtils";
import NotificacaoUtils from "../utils/NotificacaoUtils";
import api from "../services/api";
import { isSuperAdmin } from "../utils/PermissaoUtils";

const CadastroUsuario = ({ isEdit = false }) => {
    const { id } = useParams(); 
    const navigate = useNavigate();
    const [carregando, setCarregando] = useState(false);
    const [dadosUsuarioCarregados, setDadosUsuarioCarregados] = useState(false);
    const { itens: grupoPermissao, carregando: carregandoGrupoPermissao } = useGrupoPermissao();
    const { itens: tiposSistema, carregando: carregandoTiposSistema } = useTipoSistema();
    const { itens: empresa, carregando: carregandoEmpresa } = useEmpresa();

    const carregamentoGeral = () => {
      return (
        carregandoGrupoPermissao ||
        carregandoTiposSistema ||
        carregandoEmpresa ||
        carregando
      );
    };

    const [errors, setErrors] = useState({});

    const [usuario, setUsuario] = useState({
      idUsuario: 0,
      nome: '',
      cpf: '',
      grupoPermissao: '',
      empresa: '',
      login: '',
      senha: '',
      email: '',
      confirmarSenha: '',
      suporte: false,
      situacaoCadastro: true,
      mapaSistemasSuporte: new Map(),
      dataCriacao: '',
      dataAlteracao: '',
    });
    
    const buscarUsuario = async (idUsuario) => {
      setCarregando(true);
      try {
        const apiResponse = await api.get(`/usuario_/${idUsuario}`);
        if (apiResponse.success && apiResponse.data) {
          const dadosUsuario = apiResponse.data;
          
          const sistemaMap = new Map();
          if (dadosUsuario.listaSistemasSuporte?.length > 0) {
            // Usamos um Set para evitar adicionar sistemas duplicados (como o caso do ID 3 que aparece duas vezes)
            const sistemasProcessados = new Set();
            
            dadosUsuario.listaSistemasSuporte.forEach(sistema => {
              // Converter o ID para string para garantir consistência
              const sistemaId = String(sistema.id);
              
              // Só adiciona se ainda não processamos um sistema com este ID
              if (!sistemasProcessados.has(sistemaId)) {
                sistemaMap.set(sistemaId, {
                  ...sistema,
                  id: sistemaId // Garantir que o ID no objeto também seja string
                });
                sistemasProcessados.add(sistemaId);
              }
            });
          }
          
          const situacaoAtiva = dadosUsuario.situacaoCadastro === "ATIVO";
          
          setUsuario({
            ...dadosUsuario,
            situacaoCadastro: situacaoAtiva,
            senha: '',
            confirmarSenha: '',
            mapaSistemasSuporte: sistemaMap,
            empresa: String(dadosUsuario.empresa?.idEmpresa) || '',
            grupoPermissao: String(dadosUsuario.grupoPermissao?.idGrupoPermissao) || '',
          });
          
          setDadosUsuarioCarregados(true);
        } else {
          NotificacaoUtils.erro(Mensagens.USUARIO.ERRO.CARREGAR);
          navigate(APP_ROUTES.USUARIO_VISUALIZAR);
        }
      } catch (error) {
        console.error("Erro ao carregar usuário:", error);
        NotificacaoUtils.erro(Mensagens.USUARIO.ERRO.CARREGAR);
        navigate(APP_ROUTES.USUARIO_VISUALIZAR);
      } finally {
        setCarregando(false);
      }
    };
    
    useEffect(() => {
      if (isEdit && id && !dadosUsuarioCarregados) {
        buscarUsuario(id);
      }
    }, [isEdit, id, dadosUsuarioCarregados]);

    const handleInputChange = (e, field) => {
      if ((field === "senha" || field === "confirmarSenha") && isEdit && 
          (e.target.value === Mensagens.USUARIO.SENHA_PLACEHOLDER_EDICAO)) {
        setUsuario({
          ...usuario,
          [field]: '', 
        });
        return;
      }

      setUsuario({
        ...usuario,
        [field]: e.target.value,
      });

      if (errors[field]) {
        setErrors({
          ...errors,
          [field]: null,
        });
      }
    };

    const handleSwitchChange = (novoValor, field) => {
      setUsuario({
        ...usuario,
        [field]: novoValor,
      });
    }
    
    const handleUpdateCheckbox = (newMap) => {
      setUsuario({
        ...usuario,
        mapaSistemasSuporte: newMap,
        suporte: newMap.size > 0
      });
    };

    const validarFormulario = () => {
      const retornoValidacao = ValidacaoUtils.validateForm(
        usuario,
        isEdit ? TipoValidacao.USUARIO_EDITAR : TipoValidacao.USUARIO_CRIAR
      );

      if (!retornoValidacao.success) {
        setErrors(retornoValidacao.errors);
        return false;
      }

      return true;
    };

    const handleCadastro = async () => {
      if (!validarFormulario()) return;

      setCarregando(true);

      try {
        const listaSistemasSuporte = Array.from(usuario.mapaSistemasSuporte.values());
        
        const dadosUsuario = {
          idUsuario: isEdit ? usuario.idUsuario : 0,
          nome: usuario.nome,
          cpf: usuario.cpf,
          grupoPermissao: { idGrupoPermissao: usuario.grupoPermissao },
          empresa: { idEmpresa: usuario.empresa },
          login: usuario.login,
          email: usuario.email,
          suporte: usuario.suporte, 
          situacaoCadastro: usuario.situacaoCadastro ? "ATIVO" : "INATIVO",
          listaSistemasSuporte: listaSistemasSuporte,
        };

        if (usuario.senha) dadosUsuario.senha = usuario.senha;

        const apiResponse = isEdit 
          ? await Promise.resolve(api.put(`/usuario_`, dadosUsuario)) 
          : await Promise.resolve(api.post(`/usuario_`, dadosUsuario));

        if (!apiResponse.success) {
          NotificacaoUtils.erro(apiResponse.message || Mensagens.USUARIO.ERRO.ATUALIZAR);
        } else {
          NotificacaoUtils.sucesso(isEdit ? Mensagens.USUARIO.ATUALIZADO_SUCESSO : Mensagens.USUARIO.CRIADO_SUCESSO);
          navigate(APP_ROUTES.USUARIO_VISUALIZAR);
        }
      } catch (error) {
        console.error("Erro ao atualizar usuário:", error);
        NotificacaoUtils.erro(Mensagens.USUARIO.ERRO.ATUALIZAR_TENTE_NOVAMENTE);
      } finally {
        setCarregando(false);
      }
    };

    const handleCancel = () => {
      navigate(APP_ROUTES.USUARIO_VISUALIZAR);
    };

    return (
      <div>
        {carregamentoGeral() && <Carregamento />}

        <FormularioPadrao
          titulo={isEdit ? Mensagens.USUARIO.EDICAO_TITULO : Mensagens.USUARIO.CADASTRO_TITULO}
          subtitulo={
            isEdit ? Mensagens.USUARIO.EDICAO_SUBTITULO : Mensagens.USUARIO.CADASTRO_SUBTITULO
          }
          tituloInterno={Mensagens.INFORMACOES_GERAIS}
          onSalvar={handleCadastro}
          onCancelar={handleCancel}
          iconeBotao={<FaCheck />}
          dataCriacao={isEdit ? usuario.dataCriacao : null}
          dataAlteracao={isEdit ? usuario.dataAlteracao : null}
          inferiorChildren={
            <>
              <FormularioPadrao tituloInterno={Mensagens.USUARIO.INFO_LOGIN}>
                <div className="inputs-flex">
                  <div className="linha-flex">
                    <InputPadrao
                      label={Mensagens.USUARIO.LOGIN_ACESSO}
                      placeholder={Mensagens.USUARIO.LOGIN_PLACEHOLDER}
                      icone={<MdOutlineAccountCircle />}
                      required={true}
                      value={usuario.login}
                      erroMensagem={errors.login}
                      onChange={e => handleInputChange(e, 'login')}
                    />
                  </div>
                  <div className="linha-flex linha-flex-senhas">
                    <InputPadrao
                      label={Mensagens.USUARIO.SENHA}
                      placeholder={
                        isEdit
                          ? Mensagens.USUARIO.SENHA_PLACEHOLDER_EDICAO
                          : Mensagens.USUARIO.SENHA_PLACEHOLDER
                      }
                      icone={<MdLockOutline />}
                      required={!isEdit}
                      value={usuario.senha}
                      erroMensagem={errors.senha}
                      type={InputPadrao.TipoInput.PASSWORD}
                      onChange={e => handleInputChange(e, 'senha')}
                    />
                    <InputPadrao
                      label={Mensagens.USUARIO.CONFIRMAR_SENHA}
                      placeholder={
                        isEdit
                          ? Mensagens.USUARIO.CONFIRMAR_SENHA_PLACEHOLDER_EDICAO
                          : Mensagens.USUARIO.CONFIRMAR_SENHA_PLACEHOLDER
                      }
                      icone={<MdLockOutline />}
                      required={!isEdit}
                      value={usuario.confirmarSenha}
                      erroMensagem={errors.confirmarSenha}
                      type={InputPadrao.TipoInput.PASSWORD}
                      onChange={e => handleInputChange(e, 'confirmarSenha')}
                    />
                  </div>
                </div>
              </FormularioPadrao>

              <FormularioPadraoExpansivo
                tituloCard={Mensagens.USUARIO.PERMISSOES_SUPORTE}
                tituloInterno={Mensagens.SELECAO_SISTEMAS}
              >
                <div className="box-checkbox-permissoes">
                  {tiposSistema.map(sistema => {
                    // O sistema.value já é string pelo DropdownHooks.js, então usamos diretamente
                    const sistemaId = sistema.value;
                    const estaMapeado = usuario.mapaSistemasSuporte.has(sistemaId);
                    
                    return (
                      <CheckboxPadrao
                        key={sistemaId}
                        label={sistema.label}
                        valueId={sistemaId}
                        updateStateCallback={handleUpdateCheckbox}
                        itemsMap={usuario.mapaSistemasSuporte}
                      />
                    );
                  })}
                </div>
              </FormularioPadraoExpansivo>
            </>
          }
        >
          <div className="inputs-flex">
            <div className="linha-flex">
              <InputPadrao
                label={Mensagens.USUARIO.NOME}
                placeholder={Mensagens.USUARIO.NOME_PLACEHOLDER}
                required={true}
                value={usuario.nome}
                erroMensagem={errors.nome}
                onChange={e => handleInputChange(e, 'nome')}
              />
              <DropdownPadrao
                label={Mensagens.USUARIO.GRUPO_PERMISSAO}
                required={true}
                showFilter={true}
                options={grupoPermissao}
                value={usuario.grupoPermissao}
                erroMensagem={errors.grupoPermissao}
                onChange={e => handleInputChange(e, 'grupoPermissao')}
              />
            </div>

            <div className="linha-flex">
              <DropdownPadrao
                label={Mensagens.USUARIO.EMPRESA}
                required={true}
                showFilter={true}
                options={empresa}
                value={usuario.empresa}
                erroMensagem={errors.empresa}
                exibirSe={isSuperAdmin()}
                onChange={e => handleInputChange(e, 'empresa')}
              />
              <InputPadrao
                label={Mensagens.USUARIO.CPF}
                placeholder={Mensagens.USUARIO.CPF_PLACEHOLDER}
                value={usuario.cpf}
                required={true}
                tipoFormatador={TipoFormatacao.CPF}
                erroMensagem={errors.cpf}
                onChange={e => handleInputChange(e, 'cpf')}
              />
            </div>

            <div className="linha-flex">
              <InputPadrao
                label={Mensagens.USUARIO.EMAIL}
                placeholder={Mensagens.USUARIO.EMAIL_PLACEHOLDER}
                value={usuario.email}
                required={true}
                erroMensagem={errors.email}
                onChange={e => handleInputChange(e, 'email')}
              />
              <TogglePadrao
                label={Mensagens.USUARIO.SITUACAO_CADASTRO}
                value={usuario.situacaoCadastro}
                disabled={!isEdit}
                onChange={novoValor => handleSwitchChange(novoValor, 'situacaoCadastro')}
              />
            </div>
          </div>
        </FormularioPadrao>
      </div>
    );
}
 
export default CadastroUsuario;